import { create } from 'zustand';
import { Package, PackageSchema } from '../types/package';
import { collection, doc, writeBatch, updateDoc, deleteDoc, getDocs, query, where, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { setupPackagesListener } from './packages/listener';
import toast from 'react-hot-toast';

type PackagesState = {
  packages: Record<string, Package>;
  loading: boolean;
  error: string | null;
  fetchPackages: () => Promise<void>;
  uploadPackages: (data: PackageSchema[]) => Promise<{ message: string }>;
  updatePackage: (packageId: string, updates: Partial<Package>) => Promise<void>;
  deletePackage: (packageId: string) => Promise<void>;
  deleteAll: () => Promise<void>;
};

export const usePackagesStore = create<PackagesState>((set) => ({
  packages: {},
  loading: false,
  error: null,

  fetchPackages: async () => {
    set({ loading: true, error: null });
    try {
      setupPackagesListener(
        (packages) => set({ packages, loading: false }),
        (error) => set({ error: error.message, loading: false })
      );
    } catch (error: any) {
      console.error('Error fetching packages:', error);
      set({ error: error.message, loading: false });
    }
  },

  uploadPackages: async (packagesData: PackageSchema[]) => {
    set({ loading: true, error: null });
    try {
      const batch = writeBatch(firestore);
      const createdPackages: Package[] = [];

      for (const packageData of packagesData) {
        const docRef = doc(collection(firestore, 'Packages'));
        const pkg: Package = {
          id: docRef.id,
          ...packageData,
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
        
        batch.set(docRef, pkg);
        createdPackages.push(pkg);
      }

      await batch.commit();
      return { message: `تم إضافة ${createdPackages.length} باقة بنجاح` };
    } catch (error: any) {
      console.error('Error uploading packages:', error);
      set({ error: error.message, loading: false });
      throw new Error('فشل في رفع بيانات الباقات');
    }
  },

  updatePackage: async (packageId: string, updates: Partial<Package>) => {
    set({ loading: true, error: null });
    try {
      console.log('✨ Starting package update with ID:', packageId);
      console.log('📝 Update data:', updates);
      
      // 1. Get current package data to compare old and new values
      const packageRef = doc(firestore, 'Packages', packageId);
      const packageSnapshot = await getDoc(packageRef);
      
      if (!packageSnapshot.exists()) {
        throw new Error('Package not found');
      }
      
      const currentPackage = packageSnapshot.data() as Package;
      console.log('📦 Current package:', {
        name: currentPackage.name,
        mainMakkahHotel: currentPackage.mainMakkahHotel,
        mainMakkahStartDate: currentPackage.mainMakkahStartDate,
        mainMakkahEndDate: currentPackage.mainMakkahEndDate
      });
      
      const batch = writeBatch(firestore);
      
      // 2. Update package document
      const updatedData = {
        ...updates,
        updatedAt: Date.now()
      };
      
      batch.update(packageRef, updatedData);
      
      // 3. Update PilgrimSelector documents
      try {
        // Find all selectors that use this package name
        const selectorRef = collection(firestore, 'PilgrimSelector');
        const packageName = currentPackage.name;
        const selectorQuery = query(
          selectorRef,
          where('packageData.packageName', '==', packageName)
        );
        
        const selectorSnapshot = await getDocs(selectorQuery);
        console.log(`📋 Found ${selectorSnapshot.docs.length} selector documents to update`);
        
        // Process in batches of 500 (Firestore batch limit)
        const selectorBatches = [];
        let currentBatch = writeBatch(firestore);
        let operationCount = 0;
        const BATCH_LIMIT = 450;
        
        // Track pilgrims that need accommodation status updates
        const pilgrimsToUpdateAccommodation = new Map<string, {
          pilgrimId: string,
          hotelName: string,
          needsRemoval: boolean
        }>();
        
        selectorSnapshot.docs.forEach((doc) => {
          const selectorData = doc.data();
          const packageData = selectorData.packageData;
          let needsUpdate = false;
          
          // Create updated packageData object
          const updatedPackageData = { ...packageData };
          
          // Update main Makkah hotel info if changed
          if (updates.mainMakkahHotel && currentPackage.mainMakkahHotel !== updates.mainMakkahHotel && 
              updatedPackageData.mainMakkah.hotel === currentPackage.mainMakkahHotel) {
            // Mark pilgrim for accommodation update
            pilgrimsToUpdateAccommodation.set(selectorData.pilgrimId, {
              pilgrimId: selectorData.pilgrimId,
              hotelName: currentPackage.mainMakkahHotel,
              needsRemoval: true
            });
            
            updatedPackageData.mainMakkah.hotel = updates.mainMakkahHotel;
            needsUpdate = true;
          }
          
          if (updates.mainMakkahStartDate && currentPackage.mainMakkahStartDate !== updates.mainMakkahStartDate && 
              updatedPackageData.mainMakkah.startDate === currentPackage.mainMakkahStartDate) {
            updatedPackageData.mainMakkah.startDate = updates.mainMakkahStartDate;
            needsUpdate = true;
            
            // Mark pilgrim for possible accommodation update due to date change
            if (!pilgrimsToUpdateAccommodation.has(selectorData.pilgrimId)) {
              pilgrimsToUpdateAccommodation.set(selectorData.pilgrimId, {
                pilgrimId: selectorData.pilgrimId,
                hotelName: currentPackage.mainMakkahHotel,
                needsRemoval: false
              });
            }
          }
          
          if (updates.mainMakkahEndDate && currentPackage.mainMakkahEndDate !== updates.mainMakkahEndDate && 
              updatedPackageData.mainMakkah.endDate === currentPackage.mainMakkahEndDate) {
            updatedPackageData.mainMakkah.endDate = updates.mainMakkahEndDate;
            needsUpdate = true;
            
            // Mark pilgrim for possible accommodation update due to date change
            if (!pilgrimsToUpdateAccommodation.has(selectorData.pilgrimId)) {
              pilgrimsToUpdateAccommodation.set(selectorData.pilgrimId, {
                pilgrimId: selectorData.pilgrimId,
                hotelName: currentPackage.mainMakkahHotel,
                needsRemoval: false
              });
            }
          }
          
          // Update Madinah hotel info if changed
          if (updates.madinahHotel && currentPackage.madinahHotel !== updates.madinahHotel && 
              updatedPackageData.madinah.hotel === currentPackage.madinahHotel) {
            // Mark pilgrim for accommodation update
            pilgrimsToUpdateAccommodation.set(selectorData.pilgrimId, {
              pilgrimId: selectorData.pilgrimId,
              hotelName: currentPackage.madinahHotel,
              needsRemoval: true
            });
            
            updatedPackageData.madinah.hotel = updates.madinahHotel;
            needsUpdate = true;
          }
          
          if (updates.madinahStartDate && currentPackage.madinahStartDate !== updates.madinahStartDate && 
              updatedPackageData.madinah.startDate === currentPackage.madinahStartDate) {
            updatedPackageData.madinah.startDate = updates.madinahStartDate;
            needsUpdate = true;
            
            // Mark pilgrim for possible accommodation update due to date change
            if (!pilgrimsToUpdateAccommodation.has(selectorData.pilgrimId)) {
              pilgrimsToUpdateAccommodation.set(selectorData.pilgrimId, {
                pilgrimId: selectorData.pilgrimId,
                hotelName: currentPackage.madinahHotel,
                needsRemoval: false
              });
            }
          }
          
          if (updates.madinahEndDate && currentPackage.madinahEndDate !== updates.madinahEndDate && 
              updatedPackageData.madinah.endDate === currentPackage.madinahEndDate) {
            updatedPackageData.madinah.endDate = updates.madinahEndDate;
            needsUpdate = true;
            
            // Mark pilgrim for possible accommodation update due to date change
            if (!pilgrimsToUpdateAccommodation.has(selectorData.pilgrimId)) {
              pilgrimsToUpdateAccommodation.set(selectorData.pilgrimId, {
                pilgrimId: selectorData.pilgrimId,
                hotelName: currentPackage.madinahHotel,
                needsRemoval: false
              });
            }
          }
          
          // Update shifting Makkah hotel info if exists and changed
          if (currentPackage.shiftingMakkahHotel && updatedPackageData.shiftingMakkah) {
            if (updates.shiftingMakkahHotel && currentPackage.shiftingMakkahHotel !== updates.shiftingMakkahHotel && 
                updatedPackageData.shiftingMakkah.hotel === currentPackage.shiftingMakkahHotel) {
              // Mark pilgrim for accommodation update
              pilgrimsToUpdateAccommodation.set(selectorData.pilgrimId, {
                pilgrimId: selectorData.pilgrimId,
                hotelName: currentPackage.shiftingMakkahHotel,
                needsRemoval: true
              });
              
              updatedPackageData.shiftingMakkah.hotel = updates.shiftingMakkahHotel;
              needsUpdate = true;
            }
            
            if (updates.shiftingMakkahStartDate && currentPackage.shiftingMakkahStartDate !== updates.shiftingMakkahStartDate && 
                updatedPackageData.shiftingMakkah.startDate === currentPackage.shiftingMakkahStartDate) {
              updatedPackageData.shiftingMakkah.startDate = updates.shiftingMakkahStartDate;
              needsUpdate = true;
              
              // Mark pilgrim for possible accommodation update due to date change
              if (!pilgrimsToUpdateAccommodation.has(selectorData.pilgrimId)) {
                pilgrimsToUpdateAccommodation.set(selectorData.pilgrimId, {
                  pilgrimId: selectorData.pilgrimId,
                  hotelName: currentPackage.shiftingMakkahHotel,
                  needsRemoval: false
                });
              }
            }
            
            if (updates.shiftingMakkahEndDate && currentPackage.shiftingMakkahEndDate !== updates.shiftingMakkahEndDate && 
                updatedPackageData.shiftingMakkah.endDate === currentPackage.shiftingMakkahEndDate) {
              updatedPackageData.shiftingMakkah.endDate = updates.shiftingMakkahEndDate;
              needsUpdate = true;
              
              // Mark pilgrim for possible accommodation update due to date change
              if (!pilgrimsToUpdateAccommodation.has(selectorData.pilgrimId)) {
                pilgrimsToUpdateAccommodation.set(selectorData.pilgrimId, {
                  pilgrimId: selectorData.pilgrimId,
                  hotelName: currentPackage.shiftingMakkahHotel,
                  needsRemoval: false
                });
              }
            }
          }
          
          // Update hotels array if it exists
          if (selectorData.hotels && needsUpdate) {
            const updatedHotels = [...selectorData.hotels];
            
            // Update main Makkah hotel in the hotels array if changed
            if (updates.mainMakkahHotel && currentPackage.mainMakkahHotel !== updates.mainMakkahHotel) {
              const mainMakkahIndex = updatedHotels.findIndex(
                hotel => hotel === currentPackage.mainMakkahHotel
              );
              
              if (mainMakkahIndex !== -1) {
                updatedHotels[mainMakkahIndex] = updates.mainMakkahHotel;
              }
            }
            
            // Update Madinah hotel in the hotels array if changed
            if (updates.madinahHotel && currentPackage.madinahHotel !== updates.madinahHotel) {
              const madinahIndex = updatedHotels.findIndex(
                hotel => hotel === currentPackage.madinahHotel
              );
              
              if (madinahIndex !== -1) {
                updatedHotels[madinahIndex] = updates.madinahHotel;
              }
            }
            
            // Update shifting Makkah hotel in the hotels array if exists and changed
            if (updates.shiftingMakkahHotel && currentPackage.shiftingMakkahHotel !== updates.shiftingMakkahHotel && 
                selectorData.packageData.shiftingMakkah) {
              const shiftingMakkahIndex = updatedHotels.findIndex(
                hotel => hotel === currentPackage.shiftingMakkahHotel
              );
              
              if (shiftingMakkahIndex !== -1) {
                updatedHotels[shiftingMakkahIndex] = updates.shiftingMakkahHotel;
              }
            }
            
            // Only update if something changed
            if (needsUpdate) {
              currentBatch.update(doc.ref, {
                packageData: updatedPackageData,
                hotels: updatedHotels,
                updatedAt: Date.now()
              });
              operationCount++;
            }
          } else if (needsUpdate) {
            // Update package data without hotels array
            currentBatch.update(doc.ref, {
              packageData: updatedPackageData,
              updatedAt: Date.now()
            });
            operationCount++;
          }
          
          // If we've hit the batch limit, commit and create a new batch
          if (operationCount >= BATCH_LIMIT) {
            selectorBatches.push(currentBatch);
            currentBatch = writeBatch(firestore);
            operationCount = 0;
          }
        });
        
        // Add the last batch if it has operations
        if (operationCount > 0) {
          selectorBatches.push(currentBatch);
        }
        
        // Commit all batches sequentially
        for (const batchToCommit of selectorBatches) {
          await batchToCommit.commit();
        }
        
        console.log(`✅ Successfully updated ${selectorSnapshot.docs.length} selector documents`);
        console.log(`🧹 Marked ${pilgrimsToUpdateAccommodation.size} pilgrims for accommodation updates`);
        
      } catch (error) {
        console.error('❌ Error updating PilgrimSelector documents:', error);
        // Continue with other updates even if this fails
      }
      
      // 4. Update UpgradeRequests
      try {
        const upgradeRef = collection(firestore, 'UpgradeRequests');
        const upgradeBatch = writeBatch(firestore);
        let upgradeOperationCount = 0;
        let upgradeNeedsCommit = false;
        
        // Check for hotel name changes and find affected requests
        if (updates.mainMakkahHotel && currentPackage.mainMakkahHotel !== updates.mainMakkahHotel) {
          const fromQuery = query(upgradeRef, where('fromHotel', '==', currentPackage.mainMakkahHotel));
          const toQuery = query(upgradeRef, where('toHotel', '==', currentPackage.mainMakkahHotel));
          
          // Process 'from' requests
          const fromSnapshot = await getDocs(fromQuery);
          console.log(`📊 Found ${fromSnapshot.size} upgrade requests where fromHotel is the old main Makkah hotel`);
          
          fromSnapshot.docs.forEach(doc => {
            upgradeBatch.update(doc.ref, {
              fromHotel: updates.mainMakkahHotel,
              updatedAt: Date.now()
            });
            upgradeOperationCount++;
            upgradeNeedsCommit = true;
          });
          
          // Process 'to' requests
          const toSnapshot = await getDocs(toQuery);
          console.log(`📊 Found ${toSnapshot.size} upgrade requests where toHotel is the old main Makkah hotel`);
          
          toSnapshot.docs.forEach(doc => {
            upgradeBatch.update(doc.ref, {
              toHotel: updates.mainMakkahHotel,
              updatedAt: Date.now()
            });
            upgradeOperationCount++;
            upgradeNeedsCommit = true;
          });
        }
        
        if (updates.shiftingMakkahHotel && currentPackage.shiftingMakkahHotel !== updates.shiftingMakkahHotel) {
          const fromQuery = query(upgradeRef, where('fromHotel', '==', currentPackage.shiftingMakkahHotel));
          const toQuery = query(upgradeRef, where('toHotel', '==', currentPackage.shiftingMakkahHotel));
          
          // Process 'from' requests
          const fromSnapshot = await getDocs(fromQuery);
          console.log(`📊 Found ${fromSnapshot.size} upgrade requests where fromHotel is the old shifting Makkah hotel`);
          
          fromSnapshot.docs.forEach(doc => {
            upgradeBatch.update(doc.ref, {
              fromHotel: updates.shiftingMakkahHotel,
              updatedAt: Date.now()
            });
            upgradeOperationCount++;
            upgradeNeedsCommit = true;
          });
          
          // Process 'to' requests
          const toSnapshot = await getDocs(toQuery);
          console.log(`📊 Found ${toSnapshot.size} upgrade requests where toHotel is the old shifting Makkah hotel`);
          
          toSnapshot.docs.forEach(doc => {
            upgradeBatch.update(doc.ref, {
              toHotel: updates.shiftingMakkahHotel,
              updatedAt: Date.now()
            });
            upgradeOperationCount++;
            upgradeNeedsCommit = true;
          });
        }
        
        if (updates.madinahHotel && currentPackage.madinahHotel !== updates.madinahHotel) {
          const fromQuery = query(upgradeRef, where('fromHotel', '==', currentPackage.madinahHotel));
          const toQuery = query(upgradeRef, where('toHotel', '==', currentPackage.madinahHotel));
          
          // Process 'from' requests
          const fromSnapshot = await getDocs(fromQuery);
          console.log(`📊 Found ${fromSnapshot.size} upgrade requests where fromHotel is the old Madinah hotel`);
          
          fromSnapshot.docs.forEach(doc => {
            upgradeBatch.update(doc.ref, {
              fromHotel: updates.madinahHotel,
              updatedAt: Date.now()
            });
            upgradeOperationCount++;
            upgradeNeedsCommit = true;
          });
          
          // Process 'to' requests
          const toSnapshot = await getDocs(toQuery);
          console.log(`📊 Found ${toSnapshot.size} upgrade requests where toHotel is the old Madinah hotel`);
          
          toSnapshot.docs.forEach(doc => {
            upgradeBatch.update(doc.ref, {
              toHotel: updates.madinahHotel,
              updatedAt: Date.now()
            });
            upgradeOperationCount++;
            upgradeNeedsCommit = true;
          });
        }
        
        // Commit upgrade batch if needed
        if (upgradeNeedsCommit) {
          await upgradeBatch.commit();
          console.log(`✅ Updated ${upgradeOperationCount} upgrade requests`);
        } else {
          console.log(`ℹ️ No upgrade requests needed updating`);
        }
        
      } catch (error) {
        console.error('❌ Error updating UpgradeRequests:', error);
        // Continue even if this fails
      }
      
      // 5. Update AccommodationTables
      try {
        console.log(`🏨 Starting accommodation tables update`);
        
        // Helper function to convert date string dd/mm/yyyy to timestamp
        // FIX: Added type checking to ensure dateStr is a string before attempting to split it
        const dateToTimestamp = (dateStr: any): number => {
          if (!dateStr || typeof dateStr !== 'string') return 0;
          
          try {
            const parts = dateStr.split('/');
            if (parts.length !== 3) return 0;
            
            const [day, month, year] = parts.map(Number);
            return new Date(year, month - 1, day).getTime();
          } catch (error) {
            console.error('Error parsing date:', dateStr, error);
            return 0;
          }
        };
        
        // Helper function to format timestamp for logging
        const formatTimestamp = (timestamp: number): string => {
          return new Date(timestamp).toISOString();
        };
        
        // Check if any dates or hotel names have changed
        const mainMakkahHotelChanged = updates.mainMakkahHotel && currentPackage.mainMakkahHotel !== updates.mainMakkahHotel;
        const mainMakkahDatesChanged = 
          (updates.mainMakkahStartDate && currentPackage.mainMakkahStartDate !== updates.mainMakkahStartDate) || 
          (updates.mainMakkahEndDate && currentPackage.mainMakkahEndDate !== updates.mainMakkahEndDate);
          
        const shiftingMakkahHotelChanged = updates.shiftingMakkahHotel && currentPackage.shiftingMakkahHotel !== updates.shiftingMakkahHotel;
        const shiftingMakkahDatesChanged = 
          (updates.shiftingMakkahStartDate && currentPackage.shiftingMakkahStartDate !== updates.shiftingMakkahStartDate) || 
          (updates.shiftingMakkahEndDate && currentPackage.shiftingMakkahEndDate !== updates.shiftingMakkahEndDate);
          
        const madinahHotelChanged = updates.madinahHotel && currentPackage.madinahHotel !== updates.madinahHotel;
        const madinahDatesChanged = 
          (updates.madinahStartDate && currentPackage.madinahStartDate !== updates.madinahStartDate) || 
          (updates.madinahEndDate && currentPackage.madinahEndDate !== updates.madinahEndDate);
          
        console.log(`🔍 Changes detected:`, {
          mainMakkah: { hotelChanged: mainMakkahHotelChanged, datesChanged: mainMakkahDatesChanged },
          shiftingMakkah: { hotelChanged: shiftingMakkahHotelChanged, datesChanged: shiftingMakkahDatesChanged },
          madinah: { hotelChanged: madinahHotelChanged, datesChanged: madinahDatesChanged }
        });
        
        // Get tables that need updating
        const tableRef = collection(firestore, 'AccommodationTables');
        const tableQueries = [];
        
        // Create queries for each hotel that might have changed
        if (mainMakkahHotelChanged || mainMakkahDatesChanged) {
          tableQueries.push({
            query: query(tableRef, where('hn', '==', currentPackage.mainMakkahHotel)),
            oldName: currentPackage.mainMakkahHotel,
            newName: updates.mainMakkahHotel || currentPackage.mainMakkahHotel,
            oldStartDate: currentPackage.mainMakkahStartDate,
            newStartDate: updates.mainMakkahStartDate || currentPackage.mainMakkahStartDate,
            oldEndDate: currentPackage.mainMakkahEndDate,
            newEndDate: updates.mainMakkahEndDate || currentPackage.mainMakkahEndDate,
            hotelType: 'mainMakkah'
          });
        }
        
        if (shiftingMakkahHotelChanged || shiftingMakkahDatesChanged) {
          tableQueries.push({
            query: query(tableRef, where('hn', '==', currentPackage.shiftingMakkahHotel)),
            oldName: currentPackage.shiftingMakkahHotel,
            newName: updates.shiftingMakkahHotel || currentPackage.shiftingMakkahHotel,
            oldStartDate: currentPackage.shiftingMakkahStartDate,
            newStartDate: updates.shiftingMakkahStartDate || currentPackage.shiftingMakkahStartDate,
            oldEndDate: currentPackage.shiftingMakkahEndDate,
            newEndDate: updates.shiftingMakkahEndDate || currentPackage.shiftingMakkahEndDate,
            hotelType: 'shiftingMakkah'
          });
        }
        
        if (madinahHotelChanged || madinahDatesChanged) {
          tableQueries.push({
            query: query(tableRef, where('hn', '==', currentPackage.madinahHotel)),
            oldName: currentPackage.madinahHotel,
            newName: updates.madinahHotel || currentPackage.madinahHotel,
            oldStartDate: currentPackage.madinahStartDate,
            newStartDate: updates.madinahStartDate || currentPackage.madinahStartDate,
            oldEndDate: currentPackage.madinahEndDate,
            newEndDate: updates.madinahEndDate || currentPackage.madinahEndDate,
            hotelType: 'madinah'
          });
        }
        
        console.log(`🔄 Created ${tableQueries.length} queries for accommodation tables:`, 
          tableQueries.map(q => `${q.hotelType}: ${q.oldName} -> ${q.newName}`));
        
        // Track affected pilgrims for PilgrimSelector updates
        const affectedPilgrims: Record<string, { hotelName: string, accommodated: boolean }> = {};
        
        // Process each query
        for (const queryInfo of tableQueries) {
          // Convert old and new dates to timestamps for comparison
          const oldStartTimestamp = dateToTimestamp(queryInfo.oldStartDate);
          const newStartTimestamp = dateToTimestamp(queryInfo.newStartDate);
          const oldEndTimestamp = dateToTimestamp(queryInfo.oldEndDate);
          const newEndTimestamp = dateToTimestamp(queryInfo.newEndDate);
          
          console.log(`📅 Date comparison for ${queryInfo.hotelType}:`, {
            oldStart: `${queryInfo.oldStartDate} (${formatTimestamp(oldStartTimestamp)})`,
            newStart: `${queryInfo.newStartDate} (${formatTimestamp(newStartTimestamp)})`,
            oldEnd: `${queryInfo.oldEndDate} (${formatTimestamp(oldEndTimestamp)})`,
            newEnd: `${queryInfo.newEndDate} (${formatTimestamp(newEndTimestamp)})`
          });
          
          // Check if dates have changed
          const datesChanged = (
            oldStartTimestamp !== newStartTimestamp ||
            oldEndTimestamp !== newEndTimestamp
          );
          
          const hotelChanged = queryInfo.oldName !== queryInfo.newName;
          
          console.log(`📋 Processing ${queryInfo.hotelType}: dates changed: ${datesChanged}, hotel changed: ${hotelChanged}`);
          
          // Skip if neither dates nor hotel name changed
          if (!datesChanged && !hotelChanged) {
            console.log(`ℹ️ No changes needed for ${queryInfo.hotelType} - skipping`);
            continue;
          }
          
          const snapshot = await getDocs(queryInfo.query);
          console.log(`🏨 Found ${snapshot.size} accommodation tables with hotel name "${queryInfo.oldName}"`);
          
          if (snapshot.empty) continue;
          
          // Track stats for this hotel
          let tablesUpdated = 0;
          let roomsProcessed = 0;
          let assignmentsUpdated = 0;
          let assignmentsRemoved = 0;
          
          for (const tableDoc of snapshot.docs) {
            console.log(`📋 Processing table: ${tableDoc.id}`);
            const accommodationBatch = writeBatch(firestore);
            let tableNeedsUpdate = false;
            let tableUpdates: any = { ua: Date.now() };
            
            const tableData = tableDoc.data();
            
            // If hotel name changed, update it
            if (hotelChanged) {
              tableUpdates.hn = queryInfo.newName;
              tableNeedsUpdate = true;
              console.log(`🔄 Hotel name will change: ${queryInfo.oldName} -> ${queryInfo.newName}`);
              
              // When hotel name changes, we need to remove all assignments
              // since we can't transfer them to the new hotel automatically
              if (tableData.r && Array.isArray(tableData.r)) {
                console.log(`🧹 Hotel name changed, will remove all assignments`);
                
                // Track which pilgrims need their accommodation status updated
                const updatedRooms = tableData.r.map(room => {
                  let roomOccupancy = 0;
                  
                  const updatedBeds = room.b.map(bed => {
                    if (bed.a && Array.isArray(bed.a) && bed.a.length > 0) {
                      // Record all affected pilgrims
                      bed.a.forEach(assignment => {
                        console.log(`🗑️ Removing assignment for pilgrim ${assignment.pn} from room ${room.n} due to hotel name change`);
                        assignmentsRemoved++;
                        
                        // Track pilgrim for PilgrimSelector update
                        affectedPilgrims[assignment.pid] = {
                          hotelName: queryInfo.oldName,
                          accommodated: false
                        };
                      });
                      
                      // Clear all assignments
                      return { ...bed, a: [] };
                    }
                    
                    // Count occupancy
                    if (bed.a && bed.a.length > 0) {
                      roomOccupancy += 1;
                    }
                    
                    return bed;
                  });
                  
                  return { 
                    ...room, 
                    b: updatedBeds,
                    o: roomOccupancy
                  };
                });
                
                tableUpdates.r = updatedRooms;
              }
            }
            // If only dates changed, we can try to update assignments
            else if (datesChanged && tableData.r && Array.isArray(tableData.r)) {
              console.log(`🔍 Processing ${tableData.r.length} rooms for date changes`);
              
              let roomsWithChanges = 0;
              let assignmentsChecked = 0;
              let assignmentsMatchingPackage = 0;
              
              const updatedRooms = tableData.r.map(room => {
                roomsProcessed++;
                let roomChanged = false;
                let roomOccupancy = 0;
                
                // Keep track of assignments checked in this room
                let roomAssignmentsChecked = 0;
                let roomAssignmentsUpdated = 0;
                let roomAssignmentsRemoved = 0;
                
                // Update assignments in beds if dates match package dates
                const updatedBeds = room.b.map(bed => {
                  if (bed.a && Array.isArray(bed.a)) {
                    const assignmentsBefore = bed.a.length;
                    assignmentsChecked += assignmentsBefore;
                    roomAssignmentsChecked += assignmentsBefore;
                    
                    // Find the assignments that need updating
                    const updatedAssignments = [];
                    
                    bed.a.forEach(assignment => {
                      // Use a wider matching window to catch more potential assignments 
                      // from this package - up to 3 days difference to be safe
                      const isStartDateNearOld = Math.abs(assignment.ci - oldStartTimestamp) < 3 * 86400000; // Within 3 days
                      const isEndDateNearOld = Math.abs(assignment.co - oldEndTimestamp) < 3 * 86400000; // Within 3 days
                      
                      // Additional debugging to catch every assignment
                      console.log(`👁️ Checking assignment in Room ${room.n}, Pilgrim ${assignment.pn}:`, {
                        ci: formatTimestamp(assignment.ci),
                        co: formatTimestamp(assignment.co),
                        oldStartTs: formatTimestamp(oldStartTimestamp),
                        oldEndTs: formatTimestamp(oldEndTimestamp),
                        startDateNear: isStartDateNearOld,
                        endDateNear: isEndDateNearOld,
                      });
                      
                      // If this assignment matches our package dates
                      if (isStartDateNearOld && isEndDateNearOld) {
                        // Check if new dates would conflict with any existing assignments
                        let wouldCauseConflict = false;
                        
                        // First, check if the new dates go outside the acceptable range
                        const dateChange = Math.abs(newStartTimestamp - oldStartTimestamp) + 
                                           Math.abs(newEndTimestamp - oldEndTimestamp);
                                           
                        // If date change is substantial (more than 5 days), treat it as a potential conflict
                        if (dateChange > 5 * 86400000) {
                          console.log(`⚠️ Substantial date change detected (${Math.round(dateChange / 86400000)} days) - will remove assignment for pilgrim ${assignment.pn}`);
                          wouldCauseConflict = true;
                        }
                        
                        if (wouldCauseConflict) {
                          // Remove this assignment
                          console.log(`🗑️ Removing assignment for pilgrim ${assignment.pn} from room ${room.n} due to date conflict`);
                          roomAssignmentsRemoved++;
                          assignmentsRemoved++;
                          roomChanged = true;
                          
                          // Track pilgrim for PilgrimSelector update
                          affectedPilgrims[assignment.pid] = {
                            hotelName: queryInfo.oldName,
                            accommodated: false
                          };
                        } else {
                          // Update assignment dates
                          assignmentsMatchingPackage++;
                          assignmentsUpdated++;
                          roomAssignmentsUpdated++;
                          roomChanged = true;
                          
                          console.log(`✅ Updating dates for pilgrim "${assignment.pn}" in room ${room.n}:`, {
                            oldCheckIn: formatTimestamp(assignment.ci),
                            oldCheckOut: formatTimestamp(assignment.co),
                            newCheckIn: formatTimestamp(newStartTimestamp),
                            newCheckOut: formatTimestamp(newEndTimestamp)
                          });
                          
                          updatedAssignments.push({
                            ...assignment,
                            ci: newStartTimestamp,
                            co: newEndTimestamp
                          });
                          
                          roomOccupancy++;
                        }
                      } else {
                        // This assignment doesn't match our package dates, keep it as is
                        updatedAssignments.push(assignment);
                        roomOccupancy++;
                      }
                    });
                    
                    console.log(`📊 Room ${room.n}: checked ${roomAssignmentsChecked} assignments, updated ${roomAssignmentsUpdated}, removed ${roomAssignmentsRemoved}`);
                    
                    return {
                      ...bed,
                      a: updatedAssignments
                    };
                  }
                  
                  return bed;
                });
                
                if (roomChanged) {
                  roomsWithChanges++;
                }
                
                return roomChanged ? 
                  { ...room, b: updatedBeds, o: roomOccupancy } : 
                  room;
              });
              
              console.log(`📊 Table summary: ${roomsWithChanges}/${tableData.r.length} rooms modified, ${assignmentsMatchingPackage} assignments updated, ${assignmentsRemoved} removed`);
              
              if (assignmentsMatchingPackage > 0 || assignmentsRemoved > 0) {
                tableUpdates.r = updatedRooms;
                tableNeedsUpdate = true;
              }
            }
            
            // Update the table document if needed
            if (tableNeedsUpdate) {
              accommodationBatch.update(tableDoc.ref, tableUpdates);
              await accommodationBatch.commit();
              tablesUpdated++;
              console.log(`✅ Updated table: ${tableDoc.id} (${tableData.tn})`);
            } else {
              console.log(`ℹ️ No changes needed for table: ${tableDoc.id} (${tableData.tn})`);
            }
          }
          
          console.log(`📊 Summary for ${queryInfo.hotelType}:`, {
            tablesProcessed: snapshot.size,
            tablesUpdated,
            roomsProcessed,
            assignmentsUpdated,
            assignmentsRemoved
          });
        }
        
        // Update accommodation status for affected pilgrims
        if (Object.keys(affectedPilgrims).length > 0) {
          console.log(`🔄 Updating accommodation status for ${Object.keys(affectedPilgrims).length} affected pilgrims`);
          
          // Use batches to update PilgrimSelector status
          const BATCH_LIMIT = 450;
          let pilgrimBatch = writeBatch(firestore);
          let batchCount = 0;
          
          for (const [pilgrimId, info] of Object.entries(affectedPilgrims)) {
            const selectorRef = collection(firestore, 'PilgrimSelector');
            const pilgrimQuery = query(selectorRef, where('pilgrimId', '==', pilgrimId));
            const pilgrimSnapshot = await getDocs(pilgrimQuery);
            
            if (!pilgrimSnapshot.empty) {
              const pilgrimDoc = pilgrimSnapshot.docs[0];
              const pilgrimData = pilgrimDoc.data();
              
              // Update accommodation status
              let updatedAccommodation = { ...pilgrimData.isAccommodated } || {};
              updatedAccommodation[info.hotelName] = info.accommodated;
              
              pilgrimBatch.update(pilgrimDoc.ref, {
                isAccommodated: updatedAccommodation,
                updatedAt: Date.now()
              });
              
              batchCount++;
              console.log(`🔄 Updating pilgrim ${pilgrimId} accommodation status for hotel ${info.hotelName} to ${info.accommodated}`);
              
              // Commit batch if needed
              if (batchCount >= BATCH_LIMIT) {
                await pilgrimBatch.commit();
                pilgrimBatch = writeBatch(firestore);
                batchCount = 0;
              }
            }
          }
          
          if (batchCount > 0) {
            await pilgrimBatch.commit();
          }
        }
        
      } catch (error) {
        console.error('❌ Error updating AccommodationTables:', error);
        // Continue even if this fails
      }
      
      // Commit the main batch for package update
      await batch.commit();
      
      // Update local state
      set((state) => ({
        packages: {
          ...state.packages,
          [packageId]: {
            ...state.packages[packageId],
            ...updates,
            updatedAt: Date.now()
          }
        },
        loading: false
      }));
      
      console.log('✨ Package update complete with cascade updates');
      toast.success('Package updated with all related data');
      
    } catch (error: any) {
      console.error('❌ Error updating package:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deletePackage: async (packageId: string) => {
    set({ loading: true, error: null });
    try {
      await deleteDoc(doc(firestore, 'Packages', packageId));
      
      set((state) => {
        const newPackages = { ...state.packages };
        delete newPackages[packageId];
        return { packages: newPackages, loading: false };
      });
    } catch (error: any) {
      console.error('Error deleting package:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteAll: async () => {
    set({ loading: true, error: null });
    try {
      const batch = writeBatch(firestore);
      const snapshot = await getDocs(collection(firestore, 'Packages'));
      
      snapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();
      set({ packages: {}, loading: false });
    } catch (error: any) {
      console.error('Error deleting all packages:', error);
      set({ error: error.message, loading: false });
      throw new Error('فشل في حذف جميع الباقات');
    }
  }
}));